import { TYPES as APPSTATE_TYPES } from '@monofe/appstate';
import type { IContainer } from '@monofe/di';
import { buildBindable } from '@monofe/di';
import { nanoid } from '@reduxjs/toolkit';
import type { IToast, IToastMessage } from './interface';
import { TYPES as TOAST_TYPES, bindToast, injectToast, unbindToast } from './interface';
import store from './state';
import type { IModuleState } from './state/interface';
import { DEFAULT_SHOW_DURATION, DEFAULT_TYPE } from './util/constants';
import isShowing from './util/isShowing';
import { AllMessagesView } from './views';

export interface IModuleDependencies {
    state: Readonly<IModuleState>;
}

function _buildLocalDependencies(
    container: IContainer,
    dependencies: Partial<IModuleDependencies>,
): Promise<IModuleDependencies> {
    return Promise.resolve({
        state: dependencies.state ?? store(container),
    });
}

function _buildModule(deps: IModuleDependencies): Promise<IToast> {
    const Renderer = (): JSX.Element | null => {
        const messages = deps.state.useState();
        return <AllMessagesView messages={messages} />;
    };

    const module: IToast = {
        renderer: Renderer,
        show: (message: IToastMessage, duration?: number) => {
            const actionMessage = {
                text: message.text,
                id: message.id ?? nanoid(),
                type: message.type ?? DEFAULT_TYPE,
            };

            deps.state.action.displayMessage(actionMessage);

            setTimeout(() => {
                deps.state.action.hideMessage(actionMessage);
            }, duration ?? DEFAULT_SHOW_DURATION);
        },
        hide: (message: Partial<IToastMessage>) => {
            if (message.id) {
                /* Hide by id */
                deps.state.action.hideMessage({ id: message.id });
            } else if (message.text) {
                /* Hide by text content */
                for (const id of deps.state
                    .getState()
                    .filter(messageIn => messageIn.text === message.text)
                    .map(messageIn => messageIn.id)) {
                    deps.state.action.hideMessage({ id });
                }
            } else if (message.type) {
                /* Hide all matching display type */
                for (const id of deps.state
                    .getState()
                    .filter(messageIn => messageIn.type === message.type)
                    .map(messageIn => messageIn.id)) {
                    deps.state.action.hideMessage({ id });
                }
            }
        },
        update: (message: Partial<IToastMessage> & { id: string }) => {
            deps.state.action.updateMessage(message);
        },
        message: (id: string) => deps.state.getState().find(item => item.id === id),
        isShowing: message => isShowing(deps.state.getState(), message),
        allMessages: () => deps.state.getState(),
    };

    return Promise.resolve(module);
}

export async function binder(
    container: IContainer,
    dependencies: Partial<IModuleDependencies> = {},
): Promise<Readonly<IToast>> {
    const deps = await _buildLocalDependencies(container, dependencies);

    const module = await _buildModule(deps);

    return Object.freeze(module);
}

export default buildBindable<IToast>({
    id: TOAST_TYPES.IToast,
    dependencies: [APPSTATE_TYPES.IStoresReducer],
    bind: bindToast,
    remove: unbindToast,
    inject: injectToast,
    build: container => binder(container, {}),
});
